// src/pages/HeathrowAttractions.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../styles/heathrow-attractions.css";

const attractions = [
  {
    name: "Windsor Castle",
    slug: "windsor-castle",
    description:
      "One of the official residences of His Majesty The King – perfect for a half‑day trip from Heathrow.",
    distance: "7 miles from Heathrow",
    time: "15–20 mins by taxi (traffic dependent)",
    taxiFare: "Typical taxi fare: £35–£50",
    goodFor: ["History & architecture", "Short layovers", "Couples & small groups"],
    highlight:
      "Book a return taxi with waiting time included if you plan to tour the State Apartments or St George's Chapel.",
  },
  {
    name: "Legoland Windsor Resort",
    slug: "legoland-windsor",
    description:
      "Top family attraction with over 55 rides, shows and LEGO‑themed areas – ideal for day trips.",
    distance: "12 miles from Heathrow",
    time: "20–30 mins by taxi",
    taxiFare: "Typical taxi fare: £40–£55",
    goodFor: ["Families", "School holidays", "Group minibus transfers"],
    highlight:
      "CheapCabGrab can provide 7–14 seater minibuses so the whole family travels together with luggage.",
  },
  {
    name: "Kew Gardens",
    slug: "kew-gardens",
    description:
      "UNESCO World Heritage botanical gardens with glasshouses, treetop walks and seasonal light trails.",
    distance: "9 miles from Heathrow",
    time: "20–25 mins by taxi",
    taxiFare: "Typical taxi fare: £30–£45",
    goodFor: ["Nature", "Photography", "Relaxed walks"],
    highlight:
      "Ideal if you have a longer layover and want a calm outdoor space away from the terminal.",
  },
  {
    name: "Richmond Park",
    slug: "richmond-park",
    description:
      "London’s largest royal park – famous for wide open spaces, cycling routes and roaming deer.",
    distance: "10 miles from Heathrow",
    time: "20–30 mins by taxi",
    taxiFare: "Typical taxi fare: £35–£50",
    goodFor: ["Cycling", "Nature", "Picnics & viewpoints"],
    highlight:
      "Ask your driver to drop you near Richmond Gate or Pembroke Lodge for the best views over London.",
  },
  {
    name: "Thorpe Park",
    slug: "thorpe-park",
    description:
      "UK’s top thrill‑ride theme park with major rollercoasters – great for adrenaline‑seekers.",
    distance: "11 miles from Heathrow",
    time: "20–30 mins by taxi",
    taxiFare: "Typical taxi fare: £40–£55",
    goodFor: ["Teenagers & young adults", "Group days out", "Minibus transfers"],
    highlight:
      "Plan your taxi pick‑up time for park closing to avoid long queues for public transport.",
  },
  {
    name: "Hampton Court Palace",
    slug: "hampton-court-palace",
    description:
      "Historic Tudor palace on the River Thames with famous gardens and maze.",
    distance: "14 miles from Heathrow",
    time: "25–35 mins by taxi",
    taxiFare: "Typical taxi fare: £40–£60",
    goodFor: ["History", "Gardens", "Day trips"],
    highlight:
      "Combine your visit with a riverside walk – your driver can drop you close to the main visitor entrance.",
  },
];

const HeathrowAttractions = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Attractions Near Heathrow Airport",
    description:
      "Guide to attractions near Heathrow Airport including Windsor Castle, Legoland Windsor, Kew Gardens, Richmond Park, Thorpe Park and Hampton Court Palace, with taxi times and fares.",
    url: "https://www.cheapcabgrab.com/heathrow-attractions",
  };

  return (
    <div className="hat-page">
      <Helmet>
        <title>Attractions Near Heathrow Airport | CheapCabGrab</title>
        <meta
          name="description"
          content="Plan things to do near Heathrow Airport – Windsor Castle, Legoland, Kew Gardens, Richmond Park and more. Includes distance, travel time and taxi fare estimates from Heathrow."
        />
        <link
          rel="canonical"
          href="https://www.cheapcabgrab.com/heathrow-attractions"
        />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* HERO */}
      <header className="hat-hero">
        <div className="hat-hero-inner">
          <h1>🏰 Attractions Near Heathrow Airport</h1>
          <p>
            Got time before or after your flight? Heathrow is surrounded by world‑famous
            castles, parks and family attractions within 15–35 minutes by taxi.
          </p>

          <div className="hat-hero-actions">
            <Link to="/" className="hat-primary-btn">
              🚖 Get Heathrow Taxi Quote
            </Link>
            <Link to="/minibus" className="hat-secondary-btn">
              🚐 Minibus for Groups
            </Link>
            <Link to="/coach" className="hat-secondary-btn">
              🚌 Coach Hire
            </Link>
          </div>
        </div>
      </header>

      {/* QUICK INFO */}
      <section className="hat-section">
        <h2>How far can you go from Heathrow?</h2>
        <p>
          All journeys below are based on typical daytime traffic in normal conditions.
          Exact fares are calculated in real time when you book with CheapCabGrab.
        </p>

        <div className="hat-bullets">
          <div className="hat-bullet-card">
            <h3>⏱ Typical journey times</h3>
            <ul>
              <li>Most attractions: 15–35 minutes from Heathrow terminals</li>
              <li>Perfect for layovers of 6+ hours</li>
              <li>24/7 pickups from all terminals and hotels</li>
            </ul>
          </div>
          <div className="hat-bullet-card">
            <h3>💷 Fares & vehicles</h3>
            <ul>
              <li>Fixed‑price fares confirmed before you book</li>
              <li>Saloon, MPV and 7–14 seater minibuses available</li>
              <li>
                Real‑time quotes via our main form on the
                {" "}
                <Link to="/heathrow-taxi">Heathrow taxi page</Link>
              </li>
            </ul>
          </div>
          <div className="hat-bullet-card">
            <h3>🧳 Flight & terminal help</h3>
            <ul>
              <li>
                Use <Link to="/terminalFinder">Terminal Finder</Link> to confirm your
                airline’s terminal
              </li>
              <li>
                Check <Link to="/airlineContacts">Airline Contacts</Link> for delays or
                baggage queries
              </li>
              <li>
                See our <Link to="/heathrow">Heathrow guide</Link> for parking & drop‑off
                details
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ATTRACTIONS GRID */}
      <section className="hat-section">
        <h2>Popular Attractions Within 15–35 Minutes of Heathrow</h2>
        <div className="hat-grid">
          {attractions.map((a) => (
            <article key={a.slug} className="hat-card">
              <h3>{a.name}</h3>
              <p className="hat-card-desc">{a.description}</p>

              <ul className="hat-meta-list">
                <li>📍 {a.distance}</li>
                <li>⏱ {a.time}</li>
                <li>💷 {a.taxiFare}</li>
              </ul>

              <h4>Best for</h4>
              <ul className="hat-tag-list">
                {a.goodFor.map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
              </ul>

              <p className="hat-highlight">💡 {a.highlight}</p>

              <Link to="/" className="hat-card-btn">
                🚖 Book taxi from Heathrow
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* LAYOVER ADVICE */}
      <section className="hat-section hat-layover">
        <h2>Planning a Heathrow Layover?</h2>
        <div className="hat-layover-grid">
          <div>
            <h3>Short layover (4–6 hours)</h3>
            <ul>
              <li>Stay close: Windsor Castle, Kew Gardens or Richmond</li>
              <li>Allow time for security & boarding on return</li>
              <li>Book a return taxi with clear pick‑up time</li>
            </ul>
          </div>
          <div>
            <h3>Longer layover (6–10+ hours)</h3>
            <ul>
              <li>Consider Legoland or Thorpe Park for families</li>
              <li>Combine an attraction with a nearby hotel day‑room</li>
              <li>
                Use our <Link to="/heathrow-hotels">Heathrow hotels guide</Link> for
                options
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* TOOLS & SUPPORT */}
      <section className="hat-section">
        <h2>More Heathrow Tools & Support</h2>
        <div className="hat-tools-grid">
          <Link to="/" className="hat-tool-card">
            🚖 Main Taxi Quote Form
          </Link>
          <Link to="/heathrow-taxi" className="hat-tool-card">
            ✈️ Heathrow Taxi Page
          </Link>
          <Link to="/heathrow" className="hat-tool-card">
            ℹ️ Heathrow Airport Guide
          </Link>
          <Link to="/terminalFinder" className="hat-tool-card">
            🛫 Terminal Finder
          </Link>
          <Link to="/airlineContacts" className="hat-tool-card">
            📞 Airline Contacts
          </Link>
          <Link to="/minibus" className="hat-tool-card">
            🚐 Minibus Transfers
          </Link>
          <Link to="/coach" className="hat-tool-card">
            🚌 Coach Hire
          </Link>
          <Link to="/contact" className="hat-tool-card">
            📧 Contact Support
          </Link>
          <Link to="/faq" className="hat-tool-card">
            ❓ FAQs
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="hat-cta">
        <h2>Ready to book your Heathrow attraction transfer?</h2>
        <p>
          Get an instant fixed‑price quote for your journey from any Heathrow terminal or
          hotel.
        </p>
        <Link to="/" className="hat-primary-btn">
          🚖 Get Your Heathrow Taxi Quote
        </Link>
      </section>
    </div>
  );
};

export default HeathrowAttractions;


